import { PathStyleExtension } from "@deck.gl/extensions";
import { IconLayer, IconLayerProps, PathLayer, PathLayerProps } from "@deck.gl/layers";
import { FeatureGroupProps } from "react-leaflet";
import { useDispatch, useSelector } from "react-redux";

import { XYLocation } from "@nens/deck-gl";
import { doesNewPointCrossExistingLines } from "@nens/shared-utils";

import { AppDispatch } from "../../../App";
import { getBlockClick, getMouseLocation, setBlockClick } from "../../../state/map";
import { ICON_MAPPING, STATIC_COLORS } from "../DeckGlConsts";

export interface DrawLineProps extends FeatureGroupProps {
  id: "PROFILELINE" | "LEVEE";
  setCallback: (points: XYLocation[]) => void;
  confirmDrawingCallback: () => void;
  removeLastPointCallback: () => void;
  points?: XYLocation[];
  isReadyToFinalizeConfirm?: boolean;
  defaultPathLayerProps: PathLayerProps;
  finished?: boolean;
}
export const DrawLine = ({
  id,
  setCallback,
  confirmDrawingCallback,
  removeLastPointCallback,
  points = [],
  isReadyToFinalizeConfirm = true,
  defaultPathLayerProps,
  finished,
}: DrawLineProps) => {
  const dispatch = useDispatch() as AppDispatch;

  const mouseLocation = useSelector(getMouseLocation);
  const dashLine =
    mouseLocation && points.length > 0 ? [points[points.length - 1], mouseLocation] : null;
  const newLineIntersects =
    id === "LEVEE" && mouseLocation ? doesNewPointCrossExistingLines(mouseLocation, points) : false;

  const blockClick = useSelector(getBlockClick);
  !blockClick && newLineIntersects && dispatch(setBlockClick(id));
  blockClick === id && !newLineIntersects && dispatch(setBlockClick(null));

  const defaultIconLayerProps = {
    iconAtlas: "./assets/images/iconAtlas.png",
    iconMapping: ICON_MAPPING,
    sizeUnits: "pixels",
    getSize: 25,
    getPosition: (d: any) => {
      return d.coordinates;
    },
    pickable: true,
    billboard: false,
    visible: !finished,
  } as unknown as IconLayerProps;

  const createIconData = (title: string) =>
    points && points.length
      ? [
          {
            coordinates: points[points.length - 1],
            title,
          },
        ]
      : [];

  const confirmData = createIconData("Confirm drawing");
  const cancelData = createIconData("Cancel drawing");

  return [
    new PathLayer({
      ...defaultPathLayerProps,
      id: id + "-new-line-layer",
      data: [points],
    } as PathLayerProps),
    new PathLayer({
      ...defaultPathLayerProps,
      id: id + "-dash-line-layer",
      data: dashLine && !finished ? [dashLine] : [],
      getColor: () =>
        !newLineIntersects ? STATIC_COLORS.LEVEE_EDIT : STATIC_COLORS.LEVEE_EDIT_NOT_ALLOWED,
      getDashArray: [10, 10],
      dashJustified: true,
      extensions: [new PathStyleExtension({ dash: true })],
      visible: !finished,
    } as PathLayerProps),
    new IconLayer({
      ...defaultIconLayerProps,
      id: id + "-confirm-icon-layer",
      data: confirmData,
      getIcon: () => "confirm",
      onClick: () => {
        confirmDrawingCallback();
        return true;
      },
      visible: defaultIconLayerProps.visible && isReadyToFinalizeConfirm,
    }),
    new IconLayer({
      ...defaultIconLayerProps,
      id: id + "-cancel-icon-layer",
      data: cancelData,
      getIcon: () => "cancel",
      onClick: () => {
        removeLastPointCallback();
        return true;
      },
    }),
  ];
};
